/**
 * Notification Badges Component
 * Modern, minimal notification status indicators
 */

import React from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { 
  EmailOutlined as EmailIcon, 
  PersonOutline as PersonIcon,
  CheckCircleOutline as SentIcon,
  RadioButtonUnchecked as PendingIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '1px 6px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 500,
    transition: 'all 0.2s ease',
  },
  sent: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
  },
  pending: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '1px solid #ffb74d',
  },
  icon: {
    fontSize: 11,
  },
}));

const NotificationBadges = ({ 
  clientNotified, 
  engineerReminded, 
  notificationDaysBefore,
  scheduledDate 
}) => {
  const classes = useStyles();

  // Determine client notification status
  const getClientStatus = () => {
    if (notificationDaysBefore === 0 || notificationDaysBefore === null) {
      return { tooltip: 'Client notification disabled', class: classes.notNeeded, show: false };
    }
    if (clientNotified) {
      return { tooltip: `Client notified: ${new Date(clientNotified).toLocaleString()}`, class: classes.sent, show: true };
    }
    return { tooltip: 'Client notification pending', class: classes.pending, show: true };
  };

  // Determine engineer reminder status
  const getEngineerStatus = () => {
    if (engineerReminded) {
      return { tooltip: `Engineer reminded: ${new Date(engineerReminded).toLocaleString()}`, class: classes.sent, show: true };
    }
    
    // Check if reminder should have been sent (3 days before)
    if (scheduledDate) {
      const daysUntil = Math.ceil((new Date(scheduledDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 3) {
        return { tooltip: 'Engineer reminder pending', class: classes.pending, show: true };
      }
    }
    
    return { tooltip: 'Engineer reminder scheduled', class: classes.notNeeded, show: true };
  };

  const clientStatus = getClientStatus();
  const engineerStatus = getEngineerStatus();

  return (
    <Box className={classes.container}>
      {clientStatus.show && (
        <Tooltip title={clientStatus.tooltip} arrow placement="top">
          <Box className={`${classes.badge} ${clientStatus.class}`}>
            <EmailIcon className={classes.icon} />
            {clientStatus.class === classes.sent ? (
              <SentIcon className={classes.icon} />
            ) : (
              <PendingIcon className={classes.icon} />
            )}
          </Box>
        </Tooltip>
      )}
      {engineerStatus.show && (
        <Tooltip title={engineerStatus.tooltip} arrow placement="top">
          <Box className={`${classes.badge} ${engineerStatus.class}`}>
            <PersonIcon className={classes.icon} />
            {engineerStatus.class === classes.sent ? (
              <SentIcon className={classes.icon} />
            ) : (
              <PendingIcon className={classes.icon} />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default NotificationBadges;
